import type { ReactNode } from 'react'

import CenterAlignedModal from './variants/center-aligned'
import LeftAlignedModal from './variants/left-aligned'
import ConversionModal from './variants/conversion'
import FullSizedModal from './variants/full-sized'

export type TailwindMaxWidth =
  | 'tw-max-w-xs'
  | 'tw-max-w-sm'
  | 'tw-max-w-md'
  | 'tw-max-w-lg'
  | 'tw-max-w-xl'
  | 'tw-max-w-2xl'
  | 'tw-max-w-3xl'
  | 'tw-max-w-4xl'
  | 'tw-max-w-5xl'
  | 'tw-max-w-6xl'
  | 'tw-max-w-7xl'
  | 'tw-max-w-full'
  | 'tw-max-w-min'
  | 'tw-max-w-max'
  | 'tw-max-w-prose'
  | 'tw-max-w-screen-sm'
  | 'tw-max-w-screen-md'
  | 'tw-max-w-screen-lg'
  | 'tw-max-w-screen-xl'
  | 'tw-max-w-screen-2xl'

type ModalVariants = 'left' | 'center' | 'conversion' | 'full'

type ModalAction = {
  buttonText: string
  onClick: () => void
  size?: 'default' | 'lg' | 'xl' | '2xl'
  disabled?: boolean
  buttonClassName?: string
}
export type ConversionFooter = {
  primary: ModalAction
  cancel: ModalAction
}
export interface ModalProps {
  /** Indicates whether the modal is currently open */
  isOpen: boolean
  /** Function to be called when the modal is requested to close */
  onClose: () => void
  /** Function to be called when the modal is requested to close through the X button */
  onCloseX?: () => void
  /** Optional title displayed at the top of the modal */
  title?: string
  /** Optional CSS class names to style the modal title */
  titleClassName?: string
  /** Optional icon displayed alongside the title */
  icon?: ReactNode
  /** Indicates whether the close button should be displayed */
  closeButton?: boolean
  /** The main content of the modal */
  content: ReactNode
  /** Optional CSS class names to style the modal container */
  containerClassName?: string
}
interface ModalDialogProps extends ModalProps {
  /** Optional variant to control the visual style of the modal */
  variant?: ModalVariants
  /** Maximum width of the modal, defined by Tailwind CSS max-width utilities */
  maxSize?: TailwindMaxWidth
  /** Optional URL or path to an image displayed in the modal */
  image?: string
  /** Footer content of the modal, which can be either a React node or a ConversionFooter component */
  footer?: ReactNode | ConversionFooter
  /** Indicates whether the modal is in a loading state */
  isLoading?: boolean
  /** Optional variables to be passed to the title */
  titleVariables?: string[]
  /** Optional variables to set z-index */
  zIndex?: string
}

export default function ModalDialog({
  isOpen,
  onClose,
  onCloseX,
  variant = 'left',
  maxSize = 'tw-max-w-lg',
  title,
  titleClassName,
  icon,
  image,
  closeButton = true,
  footer,
  content,
  isLoading = false,
  containerClassName,
  titleVariables,
  zIndex = 'tw-z-[9999]',
}: ModalDialogProps) {
  const isConversionFooter = (
    footerElement: ReactNode | ConversionFooter,
  ): footerElement is ConversionFooter => variant === 'conversion'

  const props = {
    isOpen,
    onClose,
    onCloseX,
    maxSize,
    title,
    titleClassName,
    icon,
    closeButton,
    content,
    containerClassName,
    titleVariables,
    zIndex,
  }

  if (variant === 'full') {
    return <FullSizedModal {...props} />
  }

  if (isConversionFooter(footer)) {
    return (
      <ConversionModal
        image={image}
        isLoading={isLoading}
        footer={footer}
        {...props}
      />
    )
  }

  if (variant === 'left') {
    return <LeftAlignedModal footer={footer} {...props} />
  }

  return <CenterAlignedModal image={image} footer={footer} {...props} />
}
